import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isPrivateSite, publicAuthHeaders, publicCredential } from '@/services/publicAccess'
import { useLeague } from '@/contexts/LeagueContext'
import { addonEntitled } from '@/contexts/tenantAccess'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

interface RouteMapping {
  page_type: string
  entity_id?: number
}

function parseRoute(pathname: string): RouteMapping | null {
  // Skip admin and OBS routes
  if (pathname.startsWith('/admin') || pathname.startsWith('/obs')) {
    return null
  }

  const segments = pathname.split('/').filter(Boolean)

  // Home page
  if (segments.length === 0) {
    return { page_type: 'home' }
  }

  const [section, id] = segments

  switch (section) {
    // Member pages (registration add-on): /reset-password/<token> carries a
    // credential in its path (security-model.md quirk 60), and the others are
    // private. None of them is ever posted. The default already skips them;
    // these explicit cases pin it (usePageTracking.test.tsx).
    case 'reset-password':
    case 'forgot-password':
    case 'account':
    case 'sign-in':
    // /register/join?invite_token= carries a credential (registration plan
    // step 16), and register pages are member pages. Only the pathname is
    // ever posted, never the query.
    // falls through
    case 'register':
      return null
    case 'players':
      return id ? { page_type: 'player', entity_id: parseInt(id) || undefined } : { page_type: 'player' }
    case 'teams':
      return id ? { page_type: 'team', entity_id: parseInt(id) || undefined } : { page_type: 'team' }
    case 'game':
      return id ? { page_type: 'game', entity_id: parseInt(id) || undefined } : null
    case 'schedule':
      return { page_type: 'schedule', entity_id: id ? parseInt(id) || undefined : undefined }
    case 'gallery':
      return { page_type: 'gallery' }
    case 'blog':
      return { page_type: 'blog' }
    case 'pages':
      return { page_type: 'page' }
    case 'sponsor':
      return id ? { page_type: 'sponsor', entity_id: parseInt(id) || undefined } : null
    case 'live':
      return { page_type: 'live_event' }
    default:
      return null
  }
}

// The league, or null outside a LeagueProvider (isolated hook tests): a null
// tenant counts as entitled, as for every non-strict add-on.
function useLeagueOrNull() {
  try {
    return useLeague().league ?? null
  } catch {
    return null
  }
}

export function usePageTracking() {
  const location = useLocation()
  // Site traffic is the analytics add-on (1.0 launch, P2): without it the
  // server refuses /track, so nothing is sent.
  const tracked = addonEntitled(useLeagueOrNull(), 'analytics')

  useEffect(() => {
    if (!tracked) return
    const route = parseRoute(location.pathname)
    if (!route) return
    // A members-only league gates /track (registration step 33): without a
    // credential the POST could only be refused, so it is skipped; with one
    // it carries the Bearer. Nothing changes on a public league.
    const sent = publicCredential()
    if (isPrivateSite() && !sent) return

    try {
      fetch(`${API_BASE_URL}/public/track`, {
        method: 'POST',
        headers: { ...publicAuthHeaders(sent), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_type: route.page_type,
          entity_id: route.entity_id,
          path: location.pathname,
        }),
        keepalive: true,
      }).catch(() => {
        // Silently ignore tracking failures
      })
    } catch {
      // Silently ignore
    }
  }, [location.pathname, tracked])
}
